/**
 * Stable slide ids for keys, analytics, and tests. Order must match
 * `auth.registration.valueProp.slides` in `packages/i18n/src/locales/register.json`.
 *
 * Figma: section `4001:58292` — frames `4001:58294`, `4001:58337`, `4001:58376`,
 * `4001:58415`, `4001:58457` (Registration - Value Propositionn, left to right).
 */
export const VALUE_PROPOSITION_SLIDES = [
  { id: "reimagined", i18nIndex: 0 },
  { id: "equity", i18nIndex: 1 },
  { id: "support", i18nIndex: 2 },
  { id: "alerts", i18nIndex: 3 },
  { id: "path", i18nIndex: 4 },
] as const;

/**
 * Art in `apps/auth-shell/public/`. Order matches {@link VALUE_PROPOSITION_SLIDES} /
 * `auth.registration.valueProp.slides` (slide 1 → index 0, …).
 * These are relative paths that can be used with buildImageUrl() for CDN support.
 * CDN path: https://cdn.freedommortgage.com/content/{env}/images/servicingv2/slide-crop-*.png
 */
export const VALUE_PROPOSITION_SLIDE_IMAGES = [
  "images/servicingv2/slide-crop-1.png",
  "images/servicingv2/slide-crop-2.png",
  "images/servicingv2/slide-crop-3.png",
  "images/servicingv2/slide-crop-4.png",
  "images/servicingv2/slide-crop-5.png",
] as const;

/** Path segment after Vite `import.meta.env.BASE_URL` (no leading slash). */
export function getValuePropositionSlideImageRelativePath(slideIndex: number): string {
  const paths = VALUE_PROPOSITION_SLIDE_IMAGES;
  if (slideIndex <= 0) return paths[0];
  if (slideIndex >= paths.length) return paths.at(-1)!;
  return paths[slideIndex];
}

export type ValuePropositionSlideId =
  (typeof VALUE_PROPOSITION_SLIDES)[number]["id"];

/**
 * Stable React `key` for carousel dots. Uses canonical ids when the slide index
 * is defined in {@link VALUE_PROPOSITION_SLIDES}; otherwise uses an index-based
 * key when i18n exposes more slides than the fixed metadata array.
 */
export function getValuePropDotKey(slideIndex: number): string {
  if (slideIndex >= 0 && slideIndex < VALUE_PROPOSITION_SLIDES.length) {
    return VALUE_PROPOSITION_SLIDES[slideIndex].id;
  }
  return `value-prop-slide-${slideIndex}`;
}

/** Figma dev note: value proposition carousel auto-rotates every ~6s */
export const VALUE_PROPOSITION_AUTOPLAY_MS = 6000;
